import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { Button } from 'reactstrap';
import { cloneDeep } from 'lodash';
import { getValue } from '@truqc/content-path';
import Icon from '../../common/containers/icon';
import entityComposition from '../../common/classes/entityComposition';
import Label from './label';
import { contentSelector, labelSelector, newPathSelector, valueSelector } from '../selectors/index';
import logValueOnChange from '../commonActions/logValueOnChange';
import { collectionToTableRows, downloadCSV, getAllHeaders, normalizeValue } from './tableViewHelpers';
import { safeAppHashLink } from '../safeAppHashLink';
const resolveRowsAndCount = (value, content, path) => {
  const fromResolved = resolved => {
    if (resolved && Array.isArray(resolved.rows)) {
      const {
        rows
      } = resolved;
      if (rows.length === 0) {
        return null;
      }
      const rowCount = resolved.rowCount !== undefined ? resolved.rowCount : rows.length;
      return {
        rows,
        rowCount
      };
    }
    const rows = collectionToTableRows(resolved);
    if (rows.length === 0) {
      return null;
    }
    return {
      rows,
      rowCount: rows.length
    };
  };
  let resolved = fromResolved(value);
  if (!resolved && content != null && path) {
    resolved = fromResolved(getValue(path, content));
  }
  return resolved;
};
const MAX_CELL_DISPLAY_CHARS = 50;
const formatTableCellText = text => {
  const full = text == null ? '' : String(text);
  if (full.length <= MAX_CELL_DISPLAY_CHARS) {
    return {
      display: full,
      titleAttr: undefined
    };
  }
  return {
    display: `${full.slice(0, MAX_CELL_DISPLAY_CHARS)}…`,
    titleAttr: full
  };
};

/**
 * When a cell is an in-app hash route (`#/…`), render it as a link (validated by safeAppHashLink).
 */
const renderHashLinkCell = (plainText, titleFallback, hashHref, hasRowActions) => {
  if (!hashHref) {
    return plainText;
  }
  return /*#__PURE__*/React.createElement("a", {
    href: hashHref,
    className: "t-table-view-hash-link",
    "aria-label": "View",
    title: titleFallback,
    target: "_blank",
    rel: "noopener noreferrer",
    onClick: hasRowActions ? e => e.stopPropagation() : undefined,
    onKeyDown: hasRowActions ? e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.stopPropagation();
      }
    } : undefined
  }, "View");
};
const TableView = ({
  label: _label = '',
  value: _value = undefined,
  content: _content = null,
  path,
  type: _type = '',
  contentPath: _contentPath = undefined,
  parentContentPath: _parentContentPath = undefined,
  variables: _variables = {},
  onChange,
  pendingOperation: _pendingOperation = false,
  notificationActions: _notificationActions = null,
  showDownloadButton: _showDownloadButton = true,
  showRowCount: _showRowCount = false,
  maxRows: _maxRows = null,
  downloadFileName: _downloadFileName = 'table-data',
  postActions: _postActions = [],
  getRowViewHref: _getRowViewHref = undefined
}) => {
  const resolved = useMemo(() => resolveRowsAndCount(_value, _content, path), [_value, _content, path]);
  const postActionsList = useMemo(() => Array.isArray(_postActions) ? _postActions : [], [_postActions]);
  const hasRowActions = postActionsList.length > 0;
  const showViewLink = typeof _getRowViewHref === 'function';
  const handleRowClick = useCallback(async (row, event) => {
    if (!hasRowActions) return;
    event.stopPropagation();
    if (_pendingOperation) {
      var _notificationActions$;
      _notificationActions === null || _notificationActions === void 0 || (_notificationActions$ = _notificationActions.info) === null || _notificationActions$ === void 0 || _notificationActions$.call(_notificationActions, {
        message: 'Waiting for changes to save.'
      }, {
        autoClose: 3000
      });
      return;
    }
    await onChange({
      path,
      value: cloneDeep(row),
      changeModifiers: {
        localChange: false,
        postActions: postActionsList,
        type: _type,
        contentPath: _contentPath,
        parentContentPath: _parentContentPath,
        path,
        variables: _variables
      }
    });
  }, [hasRowActions, _pendingOperation, _notificationActions, onChange, path, _type, _contentPath, _parentContentPath, _variables, postActionsList]);
  if (!resolved) {
    return null;
  }
  const {
    rows,
    rowCount
  } = resolved;
  const headers = getAllHeaders(rows);
  const maxN = _maxRows == null ? null : Number(_maxRows);
  const displayRows = maxN == null || Number.isNaN(maxN) || maxN < 1 ? rows : rows.slice(0, maxN);
  const captionUsesDisplayCap = maxN != null && !Number.isNaN(maxN) && maxN >= 1 && displayRows.length < rows.length;
  const captionRowCount = captionUsesDisplayCap ? displayRows.length : rowCount;
  const captionRowWord = captionRowCount === 1 ? 'row' : 'rows';
  let caption = '';
  if (_showRowCount && _label) {
    caption = `${_label} (${captionRowCount} ${captionRowWord})`;
  } else if (_showRowCount) {
    caption = `(${captionRowCount} ${captionRowWord})`;
  } else {
    caption = _label || '';
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "mb-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "d-flex justify-content-between align-items-center mb-2"
  }, caption ? /*#__PURE__*/React.createElement(Label, {
    value: caption,
    className: "text-muted"
  }) : /*#__PURE__*/React.createElement("div", null), _showDownloadButton && /*#__PURE__*/React.createElement(Button, {
    color: "outline-secondary",
    size: "sm",
    onClick: e => {
      e.stopPropagation();
      downloadCSV(rows, _downloadFileName);
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "download",
    style: {
      marginRight: 6
    }
  }), "Download CSV")), /*#__PURE__*/React.createElement("div", {
    className: "table-responsive t-table-view-wrap"
  }, /*#__PURE__*/React.createElement("table", {
    className: `table table-sm table-bordered small mb-0 t-table-view${hasRowActions ? ' t-table-view--row-clickable' : ''}`
  }, /*#__PURE__*/React.createElement("thead", {
    className: "table-light"
  }, /*#__PURE__*/React.createElement("tr", null, headers.map(key => {
    const headerStr = String(key);
    const {
      display: headerDisplay,
      titleAttr: headerTitle
    } = formatTableCellText(headerStr);
    return /*#__PURE__*/React.createElement("th", {
      key: `header-${key}`,
      title: headerTitle
    }, headerDisplay);
  }), showViewLink ? /*#__PURE__*/React.createElement("th", {
    key: "header-view",
    className: "text-nowrap",
    "aria-label": "View"
  }) : null)), /*#__PURE__*/React.createElement("tbody", null, displayRows.map((row, rowIndex) => {
    const rowKey = row.id != null && row.id !== '' ? `row-${rowIndex}-id-${String(row.id)}` : `row-${rowIndex}-${JSON.stringify(row).substring(0, 50)}`;
    return /*#__PURE__*/React.createElement("tr", {
      key: rowKey,
      onClick: hasRowActions ? e => handleRowClick(row, e) : undefined
    }, headers.map(cellKey => {
      const rawCell = normalizeValue(row[cellKey]);
      const {
        display,
        titleAttr
      } = formatTableCellText(rawCell);
      const trimmed = String(rawCell).trim();
      const hashHref = trimmed.startsWith('#/') ? safeAppHashLink(trimmed) : null;
      const linkTitle = titleAttr || trimmed;
      return /*#__PURE__*/React.createElement("td", {
        key: `cell-${cellKey}-${rowKey}`,
        title: hashHref ? undefined : titleAttr
      }, renderHashLinkCell(display, linkTitle, hashHref, hasRowActions));
    }), showViewLink ? /*#__PURE__*/React.createElement("td", {
      key: `cell-view-${rowKey}`,
      className: "text-nowrap"
    }, (() => {
      const rawHref = _getRowViewHref(row);
      const href = safeAppHashLink(rawHref);
      if (!href) {
        return null;
      }
      return /*#__PURE__*/React.createElement("a", {
        href: href,
        className: "t-table-view-row-link",
        target: "_blank",
        rel: "noopener noreferrer",
        onClick: e => e.stopPropagation(),
        onKeyDown: e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
          }
        }
      }, "View");
    })()) : null);
  })))));
};
TableView.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    rowCount: PropTypes.number
  }),
  // UUID-keyed collections (merge-patch safe) — see collectionToTableRows
  PropTypes.objectOf(PropTypes.shape({}))]),
  content: PropTypes.shape({}),
  path: PropTypes.string.isRequired,
  type: PropTypes.string,
  contentPath: PropTypes.string,
  parentContentPath: PropTypes.string,
  variables: PropTypes.shape({}),
  onChange: PropTypes.func.isRequired,
  pendingOperation: PropTypes.bool,
  notificationActions: PropTypes.shape({
    info: PropTypes.func
  }),
  showDownloadButton: PropTypes.bool,
  showRowCount: PropTypes.bool,
  maxRows: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  downloadFileName: PropTypes.string,
  postActions: PropTypes.arrayOf(PropTypes.shape({})),
  getRowViewHref: PropTypes.func
};
export { TableView as UncomposedTableView };
export default entityComposition(TableView, [], {
  label: labelSelector,
  value: valueSelector,
  content: contentSelector,
  path: newPathSelector,
  onChange: logValueOnChange,
  pendingOperation: state => state.form.pendingOperation
}, ['form', 'notification']);